import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Users, Compass } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { SERVICES } from "@/lib/site";
import usePageMeta from "@/lib/usePageMeta";

export default function ServicesPage() {
  usePageMeta({
    title: "Services",
    description:
      "Five productized consulting engagements: Agile Transformation Assessment, Leadership Coaching & Advisory, Workshops, Roadmap Development, and Ongoing Retainer Advisory.",
    path: "/services",
  });
  return (
    <div data-testid="page-services">
      {/* Header */}
      <section data-testid="services-header">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <Reveal>
                <p className="overline">Services</p>
                <h1
                  data-testid="services-heading"
                  className="mt-6 font-display text-4xl sm:text-5xl lg:text-[72px] leading-[1.02] tracking-[-0.02em] text-brand-ink text-balance"
                >
                  Productized engagements.
                  <br />
                  <span className="italic font-medium">Shaped</span> to your reality.
                </h1>
              </Reveal>
              <Reveal delay={0.05}>
                <p className="mt-8 text-lg md:text-xl text-brand-muted leading-relaxed max-w-2xl">
                  Five engagement shapes. Each one designed for a specific moment in a transformation journey — from the first honest assessment to ongoing, on-call advisory.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Reveal delay={0.1}>
                <Link to="/contact" data-testid="services-header-cta" className="btn-primary">
                  Book a Discovery Call
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Bento grid of services */}
      <section className="bg-brand-surface2" data-testid="services-grid">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6 lg:gap-8">
            {SERVICES.map((s, i) => {
              const span =
                s.span === "large"
                  ? "md:col-span-4"
                  : "md:col-span-2";
              return (
                <Reveal key={s.id} delay={i * 0.06} className={span}>
                  <article
                    data-testid={`service-card-${s.id}`}
                    className="bg-white border border-brand-line p-8 md:p-12 h-full flex flex-col group transition-all duration-300 hover:border-brand-lineStrong hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(10,19,37,0.07)]"
                  >
                    <div className="flex items-center justify-between mb-8">
                      <p className="number-tag">{s.number}</p>
                      <span className="h-px w-12 bg-brand-line group-hover:bg-brand-accent transition-colors duration-300" />
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl tracking-tight leading-tight text-brand-ink">
                      {s.title}
                    </h2>
                    <p className="mt-5 text-[15px] md:text-base text-brand-muted leading-relaxed">
                      {s.summary}
                    </p>

                    <dl className="mt-8 space-y-5 border-t border-brand-line pt-6">
                      <div>
                        <dt className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-brand-accent font-semibold">
                          <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Who it's for
                        </dt>
                        <dd className="mt-2 text-sm text-brand-ink leading-relaxed">{s.audience}</dd>
                      </div>
                      <div>
                        <dt className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-brand-accent font-semibold">
                          <Target className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Outcome
                        </dt>
                        <dd className="mt-2 text-sm text-brand-ink leading-relaxed">{s.outcome}</dd>
                      </div>
                    </dl>

                    <div className="mt-10 pt-2">
                      <Link
                        to="/contact"
                        data-testid={`service-cta-${s.id}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-brand-accent hover:text-brand-ink transition-colors"
                      >
                        Discuss this engagement
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                      </Link>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How engagements work */}
      <section data-testid="services-how">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="overline">How engagements work</p>
                <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-brand-ink text-balance">
                  Clear scope. Honest pricing. Shared accountability.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-px bg-brand-line border border-brand-line">
                {[
                  { t: "Fixed-scope diagnostics", b: "Assessments and roadmaps are time-boxed and fixed-fee — no surprises, no scope creep." },
                  { t: "Retainer-based advisory", b: "Ongoing work is structured as monthly retainers with clear deliverables and rhythms." },
                  { t: "Confidential by default", b: "Every engagement starts with a mutual NDA. Names, materials, and conversations stay private." },
                  { t: "Right-sized teams", b: "I work directly with you — bringing trusted specialists in only when the work genuinely requires it." },
                ].map((row, i) => (
                  <Reveal key={row.t} delay={i * 0.05}>
                    <li className="bg-white p-6 md:p-8 flex items-start gap-6">
                      <Compass className="mt-1 h-5 w-5 text-brand-accent flex-none" strokeWidth={1.25} />
                      <div>
                        <p className="font-display text-lg tracking-tight text-brand-ink">{row.t}</p>
                        <p className="mt-1.5 text-[15px] text-brand-muted leading-relaxed">{row.b}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy text-white" data-testid="services-cta">
        <div className="container-tight py-20 md:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
            <Reveal>
              <p className="overline text-[#E8A487]">Not sure which fits?</p>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white text-balance max-w-2xl">
                Start with a conversation. The right shape will emerge.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/contact"
                data-testid="services-footer-cta"
                className="inline-flex items-center gap-2 bg-white text-brand-ink px-6 py-3.5 text-sm font-medium tracking-wide transition-all duration-200 hover:bg-[#E8A487] hover:-translate-y-[1px]"
              >
                Book a Discovery Call
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
